import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'

interface ChatMessage {
  id: string
  session_id: string
  user_id: string
  message: string
  message_type: string
  is_read: boolean
  created_at: string
}

interface ChatSession {
  id: string
  user_id: string
  session_status: string
  last_message_at: string
  created_at: string
  updated_at: string
}

export const useChat = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [session, setSession] = useState<ChatSession | null>(null)
  const [loading, setLoading] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const getOrCreateSession = useCallback(async () => {
    if (!user) return null

    try {
      // Check for existing open session
      const { data: existingSessions, error: sessionError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('session_status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)

      if (sessionError) throw sessionError

      if (existingSessions && existingSessions.length > 0) {
        setSession(existingSessions[0])
        return existingSessions[0]
      }

      // Create new session
      const { data: newSession, error: createError } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          session_status: 'open',
        })
        .select()
        .single()

      if (createError) throw createError

      setSession(newSession)
      return newSession
    } catch (error) {
      console.error('Error getting/creating session:', error)
      toast({
        title: 'Error',
        description: 'Failed to start chat session',
        variant: 'destructive',
      })
      return null
    }
  }, [user, toast])

  const loadMessages = useCallback(async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })

      if (error) throw error

      setMessages(data || [])

      // Count unread admin messages
      const unread = data?.filter((msg) => msg.message_type === 'admin' && !msg.is_read).length || 0
      setUnreadCount(unread)
    } catch (error) {
      console.error('Error loading messages:', error)
    }
  }, [])

  const sendMessage = useCallback(
    async (message: string) => {
      if (!user || !message.trim()) return

      setLoading(true)
      try {
        const currentSession = session || (await getOrCreateSession())
        if (!currentSession) return

        const { error } = await supabase.from('chat_messages').insert({
          session_id: currentSession.id,
          user_id: user.id,
          message: message.trim(),
          message_type: 'user',
        })

        if (error) throw error

        toast({
          title: 'Message sent',
          description: 'Your message has been sent to support',
        })
      } catch (error) {
        console.error('Error sending message:', error)
        toast({
          title: 'Error',
          description: 'Failed to send message',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    },
    [user, session, getOrCreateSession, toast],
  )

  const markMessagesAsRead = useCallback(async () => {
    if (!session || !user) return

    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .eq('session_id', session.id)
        .eq('message_type', 'admin')
        .eq('is_read', false)

      if (error) throw error
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking messages as read:', error)
    }
  }, [session, user])

  // Only load existing session when component first mounts with user
  useEffect(() => {
    if (user && !session) {
      loadExistingSession()
    }
  }, [user])

  const loadExistingSession = async () => {
    if (!user) return

    try {
      // Only check for existing session, don't create new one
      const { data: existingSessions, error: sessionError } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('session_status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)

      if (sessionError) throw sessionError

      if (existingSessions && existingSessions.length > 0) {
        setSession(existingSessions[0])
        loadMessages(existingSessions[0].id)
      }
    } catch (error) {
      console.error('Error loading existing session:', error)
    }
  }

  // Set up real-time subscriptions
  useEffect(() => {
    if (!session) return

    const channel = supabase
      .channel('chat-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `session_id=eq.${session.id}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage
          setMessages((prev) => [...prev, newMessage])

          if (newMessage.message_type === 'admin') {
            setUnreadCount((prev) => prev + 1)
          }
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [session])

  return {
    messages,
    session,
    loading,
    unreadCount,
    sendMessage,
    markMessagesAsRead,
    getOrCreateSession,
  }
}
